import type { CancelToken } from 'axios';

import { QuestionnairesApiFp as questionnairesApi } from './api/questionnaires-api';
import { hirerExpAxiosInstance, invoke } from './apiInstance';
import { DefaultDelay, retrier } from './apiRetry';
import type {
  CreateQuestionnaireRequest,
  PutQuestionsResponse,
  Question,
  Questionnaire,
  SelectedQuestion,
} from './models';

const api = questionnairesApi();

export interface QuestionnaireOptions {
  /** If true, prefill the questionnaire with the candidate's previous answers  */
  prefill?: boolean;
}

export async function createQuestionnaire(
  request: CreateQuestionnaireRequest,
  { prefill }: QuestionnaireOptions,
): Promise<Questionnaire> {
  const questionnaireResponse = await invoke<Questionnaire>(
    api.questionnairesCreateQuestionnaire(request, prefill),
  );
  return questionnaireResponse.data;
}

export async function getQuestionnaire(
  id: string,
  cancelToken?: CancelToken,
): Promise<Questionnaire> {
  const questionnaireResponse = await invoke(
    api.questionnairesGetQuestionnaire(id, false, { cancelToken }),
  );
  return questionnaireResponse.data;
}

export async function getQuestions(id: string): Promise<Question[]> {
  const response = await invoke(api.questionnairesGetQuestions(id));

  if (response.status === 200) {
    return response.data;
  }

  if (response.status === 204) {
    return [];
  }

  throw Error(`API request failed: ${response.statusText}`);
}

type ETag = {
  etag: string;
};

export interface SaveQuestionnaireOptions {
  questionnaireId: string;
  selectedQuestions: SelectedQuestion[];
  versionNumber: string;
  cancelToken: CancelToken;
}

export async function saveQuestionnaire({
  questionnaireId,
  selectedQuestions,
  versionNumber,
  cancelToken,
}: SaveQuestionnaireOptions): Promise<PutQuestionsResponse & ETag> {
  const response = await retrier(
    () =>
      hirerExpAxiosInstance().put(
        `/questionnaires/${questionnaireId}/questions`,
        { selectedQuestions },
        {
          headers: { 'If-Match': versionNumber },
          cancelToken,
        },
      ),
    {
      strategy: 'fixed',
      delayMs: DefaultDelay,
    },
  );

  return { ...response.data, etag: response.headers.etag };
}
